import React, { useRef } from 'react';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Button,
	Dialog,
	Link,
	Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Bowser from 'bowser';

import {
	MediaPermissionsError,
	MediaPermissionsErrorType,
	requestMediaPermissions,
} from 'mic-check';

const browser = Bowser.getParser(window.navigator.userAgent);

enum DialogType {
	explanation = 'explanation',
	unsupportedBrowser = 'unsupportedBrowser',
	systemDenied = 'systemDenied',
	userDenied = 'userDenied',
	trackError = 'trackError',
	mobileNotSupported = 'mobileNotSupported',
}

const MediaOnboardingDialog: React.FC = () => {
	const [showDialog, setShowDialog] = React.useState<DialogType | null>(null);

	const [audioAllowed, setAudioAllowed] = React.useState<boolean>(false);
	const [videoAllowed, setVideoAllowed] = React.useState<boolean>(false);

	const [errorDetails, setErrorDetails] = React.useState<
		MediaPermissionsError | undefined
	>();

	// Create wrapper refs to access values even during setTimeout
	// https://github.com/facebook/react/issues/14010
	const showDialogRef = useRef(showDialog);
	showDialogRef.current = showDialog;
	const audioAllowedRef = useRef(audioAllowed);
	audioAllowedRef.current = audioAllowed;
	const videoAllowedRef = useRef(videoAllowed);
	videoAllowedRef.current = videoAllowed;

	React.useEffect(() => {
		checkMediaPermissions();
	}, []);

	React.useEffect(() => {
		console.log('audio allowed permission changed: ', audioAllowed);
		if (audioAllowed || videoAllowed) {
			// set the default devices
			// MediaManager.findMediaDevices();
		}
	}, [audioAllowed, videoAllowed]);

	const checkForExplanationDialog = () => {
		if (
			(!audioAllowedRef.current || !videoAllowedRef.current) &&
			showDialogRef.current === null
		)
			setShowDialog(DialogType.explanation);
	};

	function detectMobilePlatform() {
		let check = false;
		(function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
		
		return check || isIpad();
	  };

	//ipads use the desktop browser so we need this hack to detect ipads
	function isIpad() {
		var isIpad = navigator.userAgent.toLowerCase().indexOf('ipad') !== -1;

		if (!isIpad && navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2) {
			return true;
		}

		return isIpad;
	}

	const checkMediaPermissions = () => {
		// TODO: listen to if there is a change on the audio/video piece?
		console.log('Browser: ' + browser.getBrowserName());
		console.log('Mobile Browser: ' + detectMobilePlatform());

		if(detectMobilePlatform())
		{
			console.log('Mobile Platform Detected');
			setShowDialog(DialogType.mobileNotSupported);
		}
		else if(browser.getBrowserName() === 'Firefox')
		{
			console.log('BrowserNotSupported: Firefox');
			setShowDialog(DialogType.unsupportedBrowser);
		}
		else
		{
			requestMediaPermissions({audio: true, video: false})
			.then(() => {
				setAudioAllowed(true);
				//setVideoAllowed(true);
				setShowDialog(null);
				window.location.replace("DeviceTest/index.html");
			})
			.catch((error: MediaPermissionsError) => {
				console.log('MediaOnboardingDialog: ', error);
				if (
					error.type ===
					MediaPermissionsErrorType.SystemPermissionDenied
				) {
					// user denied permission
					setShowDialog(DialogType.systemDenied);
				} else if (
					error.type ===
					MediaPermissionsErrorType.UserPermissionDenied
				) {
					// browser doesn't have access to devices
					setShowDialog(DialogType.userDenied);
				} else if (
					error.type ===
					MediaPermissionsErrorType.CouldNotStartVideoSource
				) {
					// most likely when other apps or tabs are using the cam/mic (mostly windows)
					setShowDialog(DialogType.trackError);
				} else {
				}
				setErrorDetails(error);
			});
		}	

		setTimeout(() => {
			checkForExplanationDialog();
		}, 500);
	};

	const _renderTryAgain = (text?: string) => {
		return (
			<div style={{ width: '100%', marginTop: 20 }}>
				<Button
					onClick={() => {
						if (browser.getBrowserName() === 'Safari') {
							// If on Safari, rechecking permissions results in glitches so just refresh the page
							window.location.reload();
						} else {
							checkMediaPermissions();
						}
					}}
					color="primary"
					style={{ float: 'right' }}
				>
					{text ? text : 'Erneut versuchen'}
				</Button>
			</div>
		);
	};

	const _renderErrorMessage = () => {
		if (!errorDetails) return null;
		return (
			<div style={{ marginTop: 10 }}>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreRoundedIcon />}
						aria-controls="panel1a-content"
						id="panel1a-header"
					>
						<Typography variant="caption" style={{ color: 'red' }}>
							Error Details
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Typography variant="caption">
							{errorDetails.name}: {errorDetails.message}
						</Typography>
					</AccordionDetails>
				</Accordion>
			</div>
		);
	};

	const _renderExplanationDialog = () => {
		return (
			<div>
				<Typography variant="h5">
					Erlaube der App dein Mikrofon zu benutzen.
				</Typography>
				<Typography variant="subtitle1">
					Die App braucht Zugriff auf dein Mikrofon, damit du dich mit den anderen Teilnehmern unterhalten kannst.
				</Typography>
			</div>
		);
	};

	const _renderUserDeniedDialog = () => {
		return (
			<div>
				<Typography variant="h5">
					Zugriff aufs Mikrofon ist blockiert.
				</Typography>
				<Typography>
					Die App benötigt Zugriff auf dein Mikrofon, damit du dich mit den anderen Teilnehmern unterhalten kannst.{' '}
					{browser.getBrowserName() !== 'Safari' && (
						<Typography>
							Klicke das Icon mit dem roten Kreuz {' '}
							<img
								alt="icon"
								src={
									'https://www.gstatic.com/meet/ic_blocked_camera_dark_f401bc8ec538ede48315b75286c1511b.svg'
								}
								style={{ display: 'inline' }}
							/>{' '}
							(Das Icon kann je nach benutzem Browser etwas anders aussehen) in der Adresszeile deines Browsers und erlaube den Zugriff. Klicke danach auf "Erneut Versuchen".
						</Typography>
					)}
				</Typography>
				{_renderErrorMessage()}
				{_renderTryAgain()}
			</div>
		);
	};

	const _renderSystemDeniedDialog = () => {
		const settingsDataByOS = {
			macOS: {
				name: 'System Preferences',
				link: 'x-apple.systempreferences:com.apple.preference.security?Privacy_Camera',
			},
		};

		return (
			<div>
				<Typography variant="h5">
					Das Mikrofon kann nicht verwendet werden!
				</Typography>
				<Typography>
					Der Browser hat eventuel keinen Zugriff auf das Mikrofon.
					Um das Problem zu beheben öffne{' '}
					{
						// @ts-ignore
						settingsDataByOS[browser.getOSName()] ? (
							<Link
								onClick={() => {
									window.open(
										// @ts-ignore
										settingsDataByOS[browser.getOSName()]
											.link,
										'_blank',
									);
								}}
							>
								{
									// @ts-ignore
									settingsDataByOS[browser.getOSName()].name
								}
							</Link>
						) : (
							'Settings'
						)
					}
					.
				</Typography>
				{_renderErrorMessage()}
				{_renderTryAgain()}
			</div>
		);
	};

	const _renderTrackErrorDialog = () => {
		return (
			<div>
				<Typography variant="h5">
					Fehler beim Zugriff auf das Mikrofon
				</Typography>
				<Typography>
					Eine Andere Applikation (Zoom, Webex) oder ein Browser Tab (Google
					Meet, Messenger Video) greift eventuel schon auf das Mikrofon zu.
					Bitte Schliesse die andere Applikation bevor du weiter machst.
				</Typography>
				{_renderErrorMessage()}
				{_renderTryAgain()}
			</div>
		);
	};

	const _renderUnsupportedBrowserErrorDialog = () => {
		return (
			<div>
				<Typography variant="h5">
					Dieser Browser wird leider nicht unterstüzt
				</Typography>
				<Typography>
					Firfox wird leider nicht unterstüzt. Bitte benutzen Sie einen anderen Browser, wie z.B. Chrome, Edge oder Safari
				</Typography>
				{_renderErrorMessage()}
				{_renderTryAgain()}
			</div>
		);
	};

	const _renderMobileNotSupportedErrorDialog = () => {
		return (
			<div>
				<Typography variant="h5">
					Mobile Geräte werden leider nicht unterstützt
				</Typography>
				<Typography>
					Die Simulation kann leider nicht auf mobilen Geräten wie Tablets oder Smartphones verwendet werden.
				</Typography>
				{_renderErrorMessage()}
				{/* {_renderTryAgain()} */}
			</div>
		);
	};

	const _renderDialogContent = () => {
		switch (showDialog) {
			case DialogType.explanation:
				return _renderExplanationDialog();
			case DialogType.systemDenied:
				return _renderSystemDeniedDialog();
			case DialogType.userDenied:
				return _renderUserDeniedDialog();
			case DialogType.trackError:
				return _renderTrackErrorDialog();
			case DialogType.unsupportedBrowser:
				return _renderUnsupportedBrowserErrorDialog();
			case DialogType.mobileNotSupported:
				return _renderMobileNotSupportedErrorDialog();
		}
	};
	return (
		<Dialog open={!!showDialog}>
			{showDialog && _renderDialogContent()}
		</Dialog>
	);
};

export default MediaOnboardingDialog;
